import React, { useState } from 'react';
import { Alert, Button, Modal, Table, Tag, message } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { API_BASE_URL, axiosInstance } from '../providers/dataProvider';

type MissingRow = {
  id: string;
  key: string;
  namespace: string;
  defaultText: string;
  missingLocales: string[];
};

type MissingReport = {
  locales: string[];
  total: number;
  missingCount: number;
  truncated?: boolean;
  data: MissingRow[];
};

interface MissingTranslationsButtonProps {
  /** API path after base, e.g. `admin/app-strings/missing-translations` */
  endpoint: string;
  resourceLabel: string;
}

export const MissingTranslationsButton: React.FC<MissingTranslationsButtonProps> = ({
  endpoint,
  resourceLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<MissingReport | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/${endpoint}`);
      setReport(data as MissingReport);
      setOpen(true);
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || 'Failed to load report',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button icon={<WarningOutlined />} loading={loading} onClick={load}>
        Missing Translations
      </Button>
      <Modal
        title={`${resourceLabel}: missing translations`}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={840}
      >
        {report && (
          <>
            <p style={{ marginBottom: 12 }}>
              Checking locales:{' '}
              {report.locales.length
                ? report.locales.map((c) => <Tag key={c}>{c}</Tag>)
                : '—'}
              <br />
              Missing {report.missingCount} / {report.total} keys
              {report.truncated
                ? ` (showing first ${report.data.length})`
                : ''}
            </p>
            {report.truncated && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Report truncated: ${report.missingCount} missing keys total, showing ${report.data.length}. Fix these first, then re-run the report.`}
              />
            )}
            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20 }}
              dataSource={report.data}
              columns={[
                { title: 'Namespace', dataIndex: 'namespace', width: 120 },
                { title: 'Key', dataIndex: 'key' },
                { title: 'EN', dataIndex: 'defaultText', ellipsis: true },
                {
                  title: 'Missing',
                  dataIndex: 'missingLocales',
                  render: (locales: string[]) =>
                    locales.map((c) => <Tag key={c} color="orange">{c}</Tag>),
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};
